import React from 'react';
import styles from './Login.module.css';
import { PrimaryButton, TextField } from '@fluentui/react';
import { apiClient, ApiClientRequestConfig } from '../Services/api-client';

interface LoginProps {
  onLoginSuccess: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const expired = localStorage.getItem('sessionExpired');
    if (expired) {
      setError('Session expired, please login again.');
      localStorage.removeItem('sessionExpired');
    }
  }, []);

  const encodeBase64 = (value: string) => {
    try {
      return window.btoa(unescape(encodeURIComponent(value)));
    } catch (err) {
      return window.btoa(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post(
        '/auth/admin/login',
        { email: username, password: encodeBase64(password) },
        { headers: { 'Content-Type': 'application/json' }, _skipAuthRefresh: true } as ApiClientRequestConfig
      );

      if (response.data?.accessToken) {
        localStorage.setItem('adminAccessToken', response.data.accessToken);
      }
      onLoginSuccess();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.error) {
        setError(data.error || 'Login failed');
        return;
      }
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginPanel}>
        <div className={styles.header}>
          <h1>Residenthood Site Manager</h1>
          <p className={styles.subtitle}>Admin Console v0.1</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <TextField
            label="Username"
            value={username}
            onChange={(e, value) => setUsername(value || '')}
            placeholder="residenthood-admin"
            disabled={loading}
            type="text"
            styles={{
              root: { marginBottom: '8px' },
              fieldGroup: { borderColor: '#d2d0ce' },
            }}
          />
          <TextField
            label="Password"
            value={password}
            onChange={(e, value) => setPassword(value || '')}
            type="password"
            placeholder="••••••••"
            disabled={loading}
            styles={{
              root: { marginBottom: '16px' },
              fieldGroup: { borderColor: '#d2d0ce' },
            }}
          />

          {error && <div className={styles.error}>{error}</div>}

          <PrimaryButton
            type="submit"
            disabled={loading}
            className={styles.submitButton}
            text={loading ? 'Logging in...' : 'Login'}
            styles={{
              root: { height: '40px', marginTop: '12px', width: '100%' },
              label: { fontSize: '14px', fontWeight: '600' },
            }}
          />
        </form>

        <div className={styles.footer}>
          <p>This is an internal admin console for Residenthood staff only.</p>
        </div>
      </div>
    </div>
  );
};
